<template>
  <div class="catalog-page">
    <section class="catalog-shell">
      <header class="catalog-header">
        <p class="eyebrow">Donkey</p>
        <h1>All Certificates</h1>
        <p class="intro">
          Search by client, route, or component name. Every card opens the live
          certificate in a new tab.
        </p>
      </header>

      <label class="search-panel" for="certificate-search">
        <span>Search certificates</span>
        <input
          id="certificate-search"
          v-model.trim="searchQuery"
          class="search-input"
          type="search"
          placeholder="Try foresters, legalwise, /vetvine, or vlad"
        />
      </label>

      <div class="catalog-meta">
        <span
          >{{ filteredCertificates.length }} of
          {{ certificates.length }} certificates</span
        >
      </div>

      <div v-if="filteredCertificates.length" class="certificate-grid">
        <a
          v-for="certificate in filteredCertificates"
          :key="certificate.path"
          :href="certificateHref(certificate.path)"
          class="certificate-card"
          target="_blank"
          rel="noopener noreferrer"
        >
          <div class="preview-frame">
            <img
              :src="certificate.preview"
              :alt="`${certificate.client} certificate preview`"
              loading="lazy"
            />
          </div>

          <div class="card-copy">
            <p class="client-name">{{ certificate.client }}</p>
            <h2>{{ certificate.title }}</h2>
            <p class="route-path">{{ certificate.path }}</p>
          </div>
        </a>
      </div>

      <div v-else class="empty-state">
        No certificates matched "{{ searchQuery }}".
      </div>
    </section>
  </div>
</template>

<script>
import certificates from "@/data/certificates";

export default {
  data() {
    return {
      certificates,
      searchQuery: ""
    };
  },
  computed: {
    filteredCertificates() {
      const query = this.searchQuery.toLowerCase();

      if (!query) {
        return this.certificates;
      }

      return this.certificates.filter(certificate =>
        certificate.searchText.includes(query)
      );
    }
  },
  methods: {
    certificateHref(path) {
      return this.$router.resolve({ path }).href;
    }
  }
};
</script>

<style scoped lang="scss">
.catalog-page {
  min-height: calc(100vh - 6rem);
  padding: 1rem 0 3rem;
  background: radial-gradient(
      circle at top left,
      rgba(233, 196, 106, 0.35),
      transparent 28rem
    ),
    linear-gradient(180deg, #f7f1e3 0%, #fffaf1 100%);
}

.catalog-shell {
  max-width: 1180px;
  margin: 0 auto;
}

.catalog-header {
  margin-bottom: 1.5rem;
}

.eyebrow {
  margin: 0 0 0.5rem;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #9a4d1f;
}

.catalog-header h1 {
  margin: 0;
  font-family: Georgia, "Times New Roman", serif;
  font-size: clamp(2.4rem, 6vw, 4rem);
  line-height: 0.95;
  color: #1f2a1f;
}

.intro {
  max-width: 42rem;
  margin: 0.9rem 0 0;
  font-size: 1rem;
  line-height: 1.6;
  color: #5d5a4f;
}

.search-panel {
  display: block;
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid rgba(31, 42, 31, 0.08);
  border-radius: 1.1rem;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 18px 40px rgba(74, 56, 28, 0.08);
}

.search-panel span {
  display: block;
  margin-bottom: 0.65rem;
  font-size: 0.86rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6a5d43;
}

.search-input {
  width: 100%;
  border: 1px solid rgba(31, 42, 31, 0.14);
  border-radius: 0.9rem;
  background: #fffdf8;
  padding: 0.95rem 1rem;
  font-size: 1rem;
  color: #1f2a1f;
}

.search-input:focus {
  outline: none;
  border-color: #9a4d1f;
  box-shadow: 0 0 0 3px rgba(154, 77, 31, 0.12);
}

.catalog-meta {
  margin-bottom: 1.25rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #6a5d43;
}

.certificate-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.25rem;
}

.certificate-card {
  display: block;
  overflow: hidden;
  border: 1px solid rgba(31, 42, 31, 0.08);
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.96);
  color: inherit;
  text-decoration: none;
  box-shadow: 0 22px 50px rgba(52, 43, 23, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.certificate-card:hover,
.certificate-card:focus-visible {
  transform: translateY(-4px);
  border-color: rgba(154, 77, 31, 0.24);
  box-shadow: 0 28px 60px rgba(52, 43, 23, 0.14);
}

.preview-frame {
  aspect-ratio: 4 / 3;
  padding: 0.85rem;
  background: linear-gradient(135deg, #f3ead6 0%, #fff7e8 100%);
}

.preview-frame img {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 0.7rem;
  object-fit: cover;
  background: #fff;
  box-shadow: 0 8px 20px rgba(26, 28, 24, 0.08);
}

.card-copy {
  padding: 1rem 1rem 1.1rem;
}

.client-name {
  margin: 0 0 0.45rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #9a4d1f;
}

.card-copy h2 {
  margin: 0 0 0.55rem;
  font-size: 1.1rem;
  line-height: 1.3;
  color: #1f2a1f;
}

.route-path {
  margin: 0;
  font-size: 0.92rem;
  color: #5d5a4f;
  word-break: break-word;
}

.empty-state {
  padding: 2rem 1.25rem;
  border: 1px dashed rgba(31, 42, 31, 0.18);
  border-radius: 1.1rem;
  background: rgba(255, 255, 255, 0.72);
  text-align: center;
  color: #5d5a4f;
}

@media (max-width: 640px) {
  .catalog-page {
    padding-top: 0;
  }

  .card-copy h2 {
    font-size: 1rem;
  }
}
</style>
